import React, { useContext, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import AppContext from '../../../Context/AppContext';
import { getPortalV2Paths } from '../../../util/portalV2';
import configData from '../../../config';
import { onExternalAnchorClick } from '../../../util/openExternalUrl';
import iconUser from '../../../Assets/img/userIcon.jpg';
import './UserMenu.css';

function QuestionIcon() {
    return (
        <svg
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
        >
            <circle cx="12" cy="12" r="9" />
            <path d="M9.5 9.2a2.5 2.5 0 0 1 4.3 1.7c0 1.6-2.3 1.8-2.3 3.3" />
            <path d="M12 17.2h.01" />
        </svg>
    );
}

function ChevronIcon() {
    return (
        <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
        >
            <path d="M6 9l6 6 6-6" />
        </svg>
    );
}

/**
 * Header user cluster: support help + profile photo + name + dropdown.
 * Photo/name come from Customer/GetCustomerProfileInfo (same as original dashboard).
 */
const BLUEPRINT_STEP_LINKS = {
    0: { label: 'Continue setup' },
    1: { label: 'Where you are' },
    2: { label: 'Where you want to go' },
    3: { label: 'How you get there' },
};

function isDashboardPath(pathname) {
    return pathname === '/dashboard' || pathname === '/dashboard-v2';
}

const UserMenu = ({ profile, avatarBust, onOpenProfile }) => {
    const { logOut, checkUserSession, setNewData } = useContext(AppContext);
    const history = useHistory();
    const location = useLocation();
    const session = checkUserSession();
    const onDashboard = isDashboardPath(location.pathname);
    const stepId = Number(session?.userStepId);
    const pendingStep = BLUEPRINT_STEP_LINKS[stepId] || null;
    const portalPaths = getPortalV2Paths();
    const [open, setOpen] = useState(false);
    const [helpOpen, setHelpOpen] = useState(false);
    const rootRef = useRef(null);

    const firstName = profile?.firstName || session?.firstName || '';
    const lastName = profile?.lastName || session?.lastName || '';
    const fullName =
        [firstName, lastName].filter(Boolean).join(' ') || 'Account';

    const cacheKey = avatarBust || profile?.pictureFileName || Date.now();
    const apiBase = String(configData.SERVER_URL || '').replace(/\/?$/, '/');
    const isLocalApi = /localhost|127\.0\.0\.1/i.test(apiBase);
    const avatarSrc = profile?.pictureFileName
        ? isLocalApi
            ? `/media/profile/${profile.pictureFileName}?${cacheKey}`
            : `${String(configData.SERVER_URL_IMG || '').replace(/\/?$/, '/')}` +
              `media/profile/${profile.pictureFileName}?${cacheKey}`
        : iconUser;

    const isActiveCoach =
        Boolean(profile?.isActiveCoach) ||
        localStorage.getItem('isActiveCoach') === 'true';

    useEffect(() => {
        if (!open && !helpOpen) return undefined;
        const onDocClick = (e) => {
            if (rootRef.current && !rootRef.current.contains(e.target)) {
                setOpen(false);
                setHelpOpen(false);
            }
        };
        document.addEventListener('mousedown', onDocClick);
        return () => document.removeEventListener('mousedown', onDocClick);
    }, [open, helpOpen]);

    const openProfile = () => {
        setOpen(false);
        if (typeof onOpenProfile === 'function') onOpenProfile();
    };

    const openDirectoryListing = () => {
        setOpen(false);
        if (typeof setNewData === 'function') {
            setNewData({
                ProfileCoach: { modalDirectiryListingShow: true },
            });
        }
    };

    const goToDashboard = () => {
        setOpen(false);
        history.push(portalPaths.dashboard);
    };

    const goToPendingStep = () => {
        if (!pendingStep) return;
        setOpen(false);
        history.push(stepId === 0 ? portalPaths.customize : portalPaths.steps);
    };

    return (
        <div className="dv2-user" ref={rootRef}>
            <button
                type="button"
                className="dv2-user__bell"
                aria-label="Support"
                aria-expanded={helpOpen}
                title="Support"
                onClick={() => {
                    setOpen(false);
                    setHelpOpen((v) => !v);
                }}
            >
                <QuestionIcon />
            </button>

            {helpOpen ? (
                <div
                    className="dv2-user__help"
                    role="dialog"
                    aria-label="Customer support"
                >
                    <p className="dv2-user__help-title">Need help?</p>
                    <p>
                        If you&apos;re having trouble with your account or
                        don&apos;t see a purchased program, USANA Customer
                        Service is here to help.
                    </p>
                    <p>
                        <strong>Monday–Friday: 8 a.m. to 5 p.m. MST</strong>
                    </p>
                    <p className="dv2-user__help-contact">
                        <a href="tel:1-888-950-9595">1-888-950-9595</a>
                        {' or '}
                        <a href="mailto:customerservice@usanainc.com">
                            customerservice@usanainc.com
                        </a>
                    </p>
                </div>
            ) : null}

            <button
                type="button"
                className="dv2-user__menu-btn"
                aria-haspopup="menu"
                aria-expanded={open}
                onClick={() => {
                    setHelpOpen(false);
                    setOpen((v) => !v);
                }}
            >
                <img
                    className="dv2-user__avatar"
                    src={avatarSrc}
                    alt=""
                    onError={(e) => {
                        e.currentTarget.src = iconUser;
                    }}
                />
                <span className="dv2-user__name">{fullName}</span>
                <span className="dv2-user__chev">
                    <ChevronIcon />
                </span>
            </button>

            {open && (
                <div className="dv2-user__dropdown" role="menu">
                    {onDashboard && pendingStep ? (
                        <button
                            type="button"
                            className="dv2-user__dropdown-item"
                            role="menuitem"
                            onClick={goToPendingStep}
                        >
                            {pendingStep.label}
                        </button>
                    ) : null}
                    {!onDashboard ? (
                        <button
                            type="button"
                            className="dv2-user__dropdown-item"
                            role="menuitem"
                            onClick={goToDashboard}
                        >
                            Go to Dashboard
                        </button>
                    ) : null}
                    {isActiveCoach ? (
                        <button
                            type="button"
                            className="dv2-user__dropdown-item"
                            role="menuitem"
                            onClick={openDirectoryListing}
                        >
                            Directory listing
                        </button>
                    ) : null}
                    <a
                        className="dv2-user__dropdown-item"
                        role="menuitem"
                        href="https://oolalife.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) =>
                            onExternalAnchorClick(e, 'https://oolalife.com')
                        }
                    >
                        Oola Store
                    </a>
                    <a
                        className="dv2-user__dropdown-item"
                        role="menuitem"
                        href={configData.FINDACOACHLINK}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) =>
                            onExternalAnchorClick(e, configData.FINDACOACHLINK)
                        }
                    >
                        Find a coach
                    </a>
                    <a
                        className="dv2-user__dropdown-item"
                        role="menuitem"
                        href="https://www.facebook.com/OolaLife/"
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) =>
                            onExternalAnchorClick(
                                e,
                                'https://www.facebook.com/OolaLife/'
                            )
                        }
                    >
                        Community
                    </a>
                    <button
                        type="button"
                        className="dv2-user__dropdown-item"
                        role="menuitem"
                        onClick={openProfile}
                    >
                        Profile
                    </button>
                    <button
                        type="button"
                        className="dv2-user__dropdown-item dv2-user__dropdown-item--danger"
                        role="menuitem"
                        onClick={() => {
                            setOpen(false);
                            logOut();
                        }}
                    >
                        Log out
                    </button>
                </div>
            )}
        </div>
    );
};

export default UserMenu;
